import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const isActive = (pathname, item) => (item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`));

/**
 * Menu latéral groupé par catégories repliables (chef / admin / salarié).
 * - `top` : entrées toujours visibles (tableau de bord…) ; `groups` : [{ key, label, icon, items }] (groupes vides ignorés).
 * - La catégorie contenant la page courante s'ouvre automatiquement ; l'état des autres est mémorisé (localStorage).
 */
export function GroupedNav({ portal, top = [], groups = [], dark = false, testId }) {
  const { pathname } = useLocation();
  const storageKey = `pag_nav_open_${portal}`;
  const [open, setOpen] = useState(() => { try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch { return {}; } });
  const visible = useMemo(() => groups.filter((g) => g.items && g.items.length), [groups]);
  const activeKey = useMemo(() => visible.find((g) => g.items.some((i) => isActive(pathname, i)))?.key, [visible, pathname]);
  useEffect(() => { if (activeKey && !open[activeKey]) setOpen((o) => ({ ...o, [activeKey]: true })); }, [activeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (k) => setOpen((o) => { const n = { ...o, [k]: !o[k] }; try { localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(Object.entries(n).filter(([, v]) => v)))); } catch { /* quota */ } return n; });

  const linkCls = ({ isActive: a }) => `flex items-center gap-3 px-3 py-2 text-sm font-semibold transition-colors ${a ? (dark ? "bg-white text-[#0A0A0A]" : "bg-[#002FA7] text-white") : (dark ? "text-white/70 hover:bg-white/10 hover:text-white" : "text-[#0A0A0A] hover:bg-[#F8F9FA]")}`;
  const tid = (item) => (testId ? testId(item) : `${portal}-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`);

  return (
    <nav className="flex-1 p-3 overflow-y-auto space-y-1" data-testid={`${portal}-grouped-nav`}>
      {top.map((item) => <NavLink key={item.to} to={item.to} end={item.end} data-testid={tid(item)} className={linkCls}><item.icon className="h-4 w-4" />{item.label}</NavLink>)}
      {visible.map((g) => {
        const isOpen = !!open[g.key];
        const hasActive = g.key === activeKey;
        return (
          <div key={g.key} className={`${dark ? "border-white/10" : "border-[#F1F3F5]"} border-t pt-1 mt-1`} data-testid={`${portal}-navgroup-${g.key}`} data-open={isOpen ? "1" : "0"}>
            <button type="button" onClick={() => toggle(g.key)} aria-expanded={isOpen} data-testid={`${portal}-navgroup-toggle-${g.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${hasActive && !isOpen ? (dark ? "text-[#FFD700]" : "text-[#002FA7]") : (dark ? "text-white/50 hover:text-white" : "text-[#868E96] hover:text-[#0A0A0A]")}`}>
              <g.icon className="h-4 w-4" />
              <span className="flex-1 text-left truncate">{g.label}</span>
              <span className={`text-[10px] font-mono ${dark ? "text-white/30" : "text-[#ADB5BD]"}`}>{g.items.length}</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            <div className="nav-group-body" data-open={isOpen ? "true" : "false"}>
              <div>
                <div className={`ml-3 pl-2 border-l space-y-0.5 pb-1 ${dark ? "border-white/10" : "border-[#DEE2E6]"}`}>
                  {g.items.map((item) => <NavLink key={item.to} to={item.to} end={item.end} data-testid={tid(item)} className={linkCls}><item.icon className="h-4 w-4" />{item.label}</NavLink>)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}
